import React, { useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2, Edit3, Hexagon, Link2, Loader2, Plus, Route, Search, Unlink, XCircle
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { vehicleControlCenterService } from '../../services/vehicleControlCenterService'
import type {
  AssociationSourceType,
  ControlCenterSource,
  ControlCenterZone,
  VehicleSource,
  ZoneOption,
  ZoneType
} from '../../services/vehicleControlCenterTypes'

interface Props {
  vehicleSource: VehicleSource
  vehicleId: number
  geofences: ControlCenterZone[]
  corridors: ControlCenterZone[]
  loading?: boolean
  error?: string | null
  onRefresh: () => Promise<void> | void
  onCreateZone?: (type: ZoneType) => void
  onManageZone?: (zone: ControlCenterZone, type: ZoneType) => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const sourceType = (source: ControlCenterSource | AssociationSourceType | string): string =>
  typeof source === 'string' ? source : source.source_type || source.type || 'DIRECT'

const sourceName = (source: ControlCenterSource | AssociationSourceType | string): string =>
  typeof source === 'string' ? source : source.name || String(source.id || sourceType(source))

const SOURCE_STYLE: Record<string, string> = {
  DIRECT: 'bg-blue-100 text-blue-800',
  FLEET: 'bg-violet-100 text-violet-800',
  MISSION: 'bg-orange-100 text-orange-800',
  TRACKER: 'bg-cyan-100 text-cyan-800',
  TRANSPORT: 'bg-emerald-100 text-emerald-800'
}

const RULE_TYPES: Record<ZoneType, string[]> = {
  geofence: ['GEOFENCE_ENTRY', 'GEOFENCE_EXIT', 'DWELL_TIME'],
  corridor: ['CORRIDOR_DEVIATION']
}
const DEFAULT_RULE_TYPES: Record<ZoneType, string[]> = {
  geofence: ['GEOFENCE_ENTRY', 'GEOFENCE_EXIT'],
  corridor: ['CORRIDOR_DEVIATION']
}

const VehicleZonesTab: React.FC<Props> = ({
  vehicleSource, vehicleId, geofences, corridors, loading, error, onRefresh, onCreateZone, onManageZone, showMessage
}) => {
  const { t } = useTranslation()
  const [attachType, setAttachType] = useState<ZoneType | null>(null)
  const [options, setOptions] = useState<ZoneOption[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [selectedRules, setSelectedRules] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(false)

  useEffect(() => {
    if (!attachType) return
    setOptionsLoading(true)
    setSelectedId('')
    setSelectedRules(DEFAULT_RULE_TYPES[attachType])
    vehicleControlCenterService.listZones(attachType)
      .then(setOptions)
      .catch(() => {
        setOptions([])
        showMessage?.(t('vcc_zone_options_failed', { defaultValue: 'Could not load available zones' }), 'error')
      })
      .finally(() => setOptionsLoading(false))
  }, [attachType, showMessage, t])

  const attached = useMemo(() => new Set([
    ...geofences.map(zone => `geofence:${zone.id}`),
    ...corridors.map(zone => `corridor:${zone.id}`)
  ]), [geofences, corridors])

  const filteredOptions = options.filter(option =>
    option.is_active !== false &&
    !attached.has(`${attachType}:${option.id}`) &&
    option.name.toLowerCase().includes(search.toLowerCase())
  )

  const attach = async () => {
    if (!attachType || !selectedId) return
    setBusy(true)
    try {
      await vehicleControlCenterService.attachZone(vehicleSource, vehicleId, {
        zone_type: attachType,
        zone_id: selectedId,
        rule_types: selectedRules
      })
      showMessage?.(t('vcc_zone_attached', { defaultValue: 'Zone attached to vehicle' }), 'success')
      setAttachType(null)
      await onRefresh()
    } catch (e: any) {
      showMessage?.(e?.response?.data?.error || t('vcc_zone_attach_failed', { defaultValue: 'Failed to attach zone' }), 'error')
    } finally {
      setBusy(false)
    }
  }

  const detach = async (zone: ControlCenterZone, type: ZoneType) => {
    if (!window.confirm(t('vcc_detach_confirm', { defaultValue: 'Detach "{{name}}" from this vehicle? The zone geometry will not be deleted.', name: zone.name }) as string)) return
    setBusy(true)
    try {
      await vehicleControlCenterService.detachZone(vehicleSource, vehicleId, type, zone.id)
      showMessage?.(t('vcc_zone_detached', { defaultValue: 'Zone detached from vehicle' }), 'success')
      await onRefresh()
    } catch (e: any) {
      showMessage?.(e?.response?.data?.error || t('vcc_zone_detach_failed', { defaultValue: 'Failed to detach zone' }), 'error')
    } finally {
      setBusy(false)
    }
  }

  const renderZone = (zone: ControlCenterZone, type: ZoneType) => {
    const Icon = type === 'geofence' ? Hexagon : Route
    const sources = zone.sources?.length ? zone.sources : ['DIRECT']
    return (
      <article key={`${type}-${zone.id}`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className={`rounded-lg p-2 ${type === 'geofence' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'}`}><Icon size={19} /></div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="font-semibold text-gray-900">{zone.name}</h4>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${zone.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                {zone.is_active ? t('active', { defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Inactive' })}
              </span>
              {zone.state && <span className="text-xs text-gray-500">{zone.state}</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {sources.map((source, index) => {
                const kind = sourceType(source).toUpperCase()
                return <span key={`${kind}-${index}`} title={sourceName(source)} className={`rounded px-2 py-0.5 text-[10px] font-bold ${SOURCE_STYLE[kind] || 'bg-gray-100 text-gray-700'}`}>{kind}</span>
              })}
            </div>
            <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-2">
              <div><span className="font-semibold text-gray-700">{t('vcc_triggers', { defaultValue: 'Triggers' })}: </span>{zone.trigger_types?.join(', ') || zone.rules?.map(r => r.alert_type || r.trigger).filter(Boolean).join(', ') || '—'}</div>
              <div><span className="font-semibold text-gray-700">{t('vcc_rules', { defaultValue: 'Rules' })}: </span>{zone.rule_ids?.length || zone.rules?.length || 0}</div>
            </div>
            {!zone.can_detach && zone.reason && (
              <p className="mt-3 rounded-md bg-gray-50 px-2 py-1.5 text-xs text-gray-600">
                {zone.reason} {t('vcc_manage_source', { defaultValue: 'Manage this association at its fleet, mission, or tracker source.' })}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button onClick={() => onManageZone?.(zone, type)} className="rounded-lg p-2 text-blue-700 hover:bg-blue-50" title={t('vcc_manage_zone', { defaultValue: 'Open and manage zone' }) as string}>
              <Edit3 size={17} />
            </button>
            {zone.can_detach && (
              <button disabled={busy} onClick={() => detach(zone, type)} className="rounded-lg p-2 text-orange-600 hover:bg-orange-50 disabled:opacity-50" title={t('vcc_detach', { defaultValue: 'Detach from vehicle' }) as string}>
                <Unlink size={17} />
              </button>
            )}
          </div>
        </div>
      </article>
    )
  }

  if (loading) return <div className="flex h-56 items-center justify-center gap-2 text-gray-500"><Loader2 className="animate-spin" />{t('vcc_loading', { defaultValue: 'Loading control center…' })}</div>
  if (error) return <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-center text-red-800"><XCircle className="mx-auto mb-2" /><p>{error}</p><button onClick={() => onRefresh()} className="mt-3 rounded-lg bg-red-700 px-3 py-2 text-sm text-white">{t('retry', { defaultValue: 'Retry' })}</button></div>

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('vcc_zones_routes', { defaultValue: 'Zones & Routes' })}</h3>
          <p className="text-sm text-gray-500">{t('vcc_zones_hint', { defaultValue: 'Effective direct and inherited monitoring areas for this vehicle.' })}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setAttachType('geofence')} className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"><Link2 size={15} />{t('vcc_attach_geofence', { defaultValue: 'Attach geofence' })}</button>
          <button onClick={() => setAttachType('corridor')} className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"><Link2 size={15} />{t('vcc_attach_corridor', { defaultValue: 'Attach corridor' })}</button>
          <button onClick={() => onCreateZone?.('geofence')} className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-sm text-white hover:bg-violet-700"><Plus size={15} />{t('vcc_create_geofence', { defaultValue: 'Create geofence' })}</button>
          <button onClick={() => onCreateZone?.('corridor')} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"><Plus size={15} />{t('vcc_create_corridor', { defaultValue: 'Create corridor' })}</button>
        </div>
      </div>

      {geofences.length + corridors.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 py-14 text-center text-gray-500">
          <Hexagon className="mx-auto mb-3 text-gray-300" size={38} />
          <p className="font-medium text-gray-700">{t('vcc_no_zones', { defaultValue: 'No zones or routes apply to this vehicle' })}</p>
          <p className="mt-1 text-sm">{t('vcc_no_zones_hint', { defaultValue: 'Attach an existing area or create one from the fleet map.' })}</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {geofences.map(zone => renderZone(zone, 'geofence'))}
          {corridors.map(zone => renderZone(zone, 'corridor'))}
        </div>
      )}

      {attachType && (
        <div className="fixed inset-0 z-[320] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900">{t('vcc_attach_existing', { defaultValue: 'Attach existing {{type}}', type: attachType })}</h3>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search', { defaultValue: 'Search' }) as string} className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm" />
            </div>
            <div className="mt-3 max-h-48 overflow-auto rounded-lg border border-gray-200">
              {optionsLoading ? <div className="p-6 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" /></div> :
                filteredOptions.length === 0 ? <p className="p-6 text-center text-sm text-gray-500">{t('vcc_no_available_zones', { defaultValue: 'No available zones found' })}</p> :
                  filteredOptions.map(option => (
                    <label key={option.id} className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2.5 last:border-0 hover:bg-gray-50">
                      <input type="radio" name="zone" checked={selectedId === String(option.id)} onChange={() => setSelectedId(String(option.id))} />
                      <span className="flex-1 text-sm font-medium text-gray-800">{option.name}</span>
                      {option.is_active === false && <span className="text-xs text-gray-400">{t('inactive', { defaultValue: 'Inactive' })}</span>}
                    </label>
                  ))}
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{t('vcc_create_rules', { defaultValue: 'Create monitoring rules' })}</p>
              <div className="flex flex-wrap gap-2">
                {RULE_TYPES[attachType].map(rule => (
                  <button key={rule} onClick={() => setSelectedRules(previous => previous.includes(rule) ? previous.filter(item => item !== rule) : [...previous, rule])}
                    className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${selectedRules.includes(rule) ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'}`}>
                    {selectedRules.includes(rule) && <CheckCircle2 size={12} />}{rule.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button disabled={busy} onClick={() => setAttachType(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">{t('cancel')}</button>
              <button disabled={!selectedId || busy} onClick={attach} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">{busy ? t('loading') : t('vcc_attach', { defaultValue: 'Attach' })}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VehicleZonesTab
